import * as React from "react";
import { WidgetWrapper, useEffectWithPolling } from "uxp/components";
import { IUIPropertyDefinitionMap, IWidgetTemplateProps, registerCardTemplate, wrapPropertyEditor } from "../../../common/common";
import { IDataSource, IDataSourceBinding, executeAndBind } from "../../../common/data-source-picker/datasource";
import { generateIconProp } from "../../../common/icon-selector/IconEditor";
import { SelectableItem, selectUIProps } from "../../../common/SelectableItem";
import { ContainerProperty, extractContainerProperties, IContainerPropertyProps } from "../../../common/ui-props/ContainerProperty";
import { extractTextProperties, ITextProperty, TextProperty } from "../../../common/ui-props/TextProperty";
import { convertColorStyles, replaceWithData } from "../../../../util";
import { IContextData, appendRowToContextData, useInitialContextData } from "../../../../hooks/useContextData";
import { defaultMapConfigs, defaultMapData, IMapConfigProps, MapUI } from "../../../common/map/MapUI";
import { MapPropertyEditor } from "../../../common/map/MapPropertyEditor";
import './map.scss';

interface IMapWidgetProps {
    title: ITextProperty;
    headerContainer: IContainerPropertyProps;
    defaultMarkerColor: string;
    datasources: IDataSource[];
    mapProperties?: {
        source: IDataSourceBinding;
        mapConfig: IMapConfigProps;
        refreshInterval: string;
    }
    refreshInterval: string;
    mapConfig: IMapConfigProps;
    source: IDataSourceBinding;
}

export const MapWidget: React.FunctionComponent<IWidgetTemplateProps<IMapWidgetProps>> = (props) => {

    const { uxpContext, uiProps, designer } = props

    const [initialSource, setIntitialSource] = React.useState(props.initialSource);
    const [loading, setLoading] = React.useState(false)
    const [contextData, setContextData] = React.useState<IContextData>({})
    const [mapData, setMapData] = React.useState<any[]>([])

    const { isLoading, initialContextData } = useInitialContextData(props, [props?.uiProps?.source])

    const XPRProps: IUIPropertyDefinitionMap = {
        'headerContainer': ContainerProperty({
            background: {
                color: 'rgba(1,1,1,0.5)',
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            },
        }),
        'title': TextProperty({
            text: '',
            color: '#424242',
            size: '12px'
        }, 'Title'),
        'mapProperties': wrapPropertyEditor(MapPropertyEditor, {
            source: uiProps?.mapProperties?.source || uiProps?.source || null,
            mapConfig: uiProps?.mapProperties?.mapConfig || uiProps?.mapConfig || defaultMapConfigs,
            refreshInterval: uiProps?.mapProperties?.refreshInterval || uiProps?.refreshInterval || '2000'
        }, ['fieldLabel', 'mapFieldHeight', 'locationLabel'])
    };

    let mapConfig = uiProps?.mapProperties?.mapConfig || uiProps?.mapConfig || defaultMapConfigs;

    let { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer);
    let { text: title, styles: titleStyles } = extractTextProperties(uiProps.title, 'Map Widget');
    title = replaceWithData(title, contextData)

    let refreshInterval = Number(uiProps?.mapProperties?.refreshInterval || uiProps?.refreshInterval) || 2000;

    const isInDesignerMode = () => designer && (designer.mode == 'preview' || designer.mode == 'thumbnail')
    const isInThumbnailMode = () => designer && (designer.mode == 'thumbnail')

    useEffectWithPolling(uxpContext, "mapData", refreshInterval || 6000, async () => {
        if (isInThumbnailMode()) {
            setMapData(defaultMapData)
            return;
        }

        if (!uiProps?.mapProperties?.source && !uiProps?.source) {
            setMapData([])
            return;
        }

        executeAndBind(props.uxpContext, uiProps?.mapProperties?.source || uiProps?.source, uiProps.datasources, {})
            .then(res => {
                if (res) {
                    setMapData(res)
                } else {
                    setMapData([])
                }
            })
    }, [designer]);

    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])


    function onMarkPosition(lat: number | undefined, lng: number | undefined) {
        if (mapConfig.autoFillIndexBeingMarkedOnTheMap == null) return;
        if (!lat || !lng) return

        let marker = mapConfig.autoFillMarkersConfig?.[mapConfig.autoFillIndexBeingMarkedOnTheMap];

        let _mapConfig: IMapConfigProps = {
            ...mapConfig,
            autoFillIndexBeingMarkedOnTheMap: null,
            autoFillMarkersConfig: [...mapConfig.autoFillMarkersConfig]
        }

        Object.assign(marker, {
            autoFillX: lng,
            autoFillY: lat
        })

        const mapProps = uiProps.hasOwnProperty('mapProperties')
            ? { mapProperties: { ...uiProps.mapProperties, mapConfig: _mapConfig } }
            : { mapConfig: _mapConfig }

        designer?.setUIProps({
            ...uiProps,
            ...mapProps
        })
    }

    return <WidgetWrapper className="map-widget-template">
        <SelectableItem
            preventLosingFocusOnNextClick={true}
            designer={designer}
            props={selectUIProps(uiProps, XPRProps, 'mapProperties')}
            contextData={appendRowToContextData(contextData, props, uiProps?.mapProperties?.source || uiProps?.source)}
        >
            <MapUI
                uxpContext={props.uxpContext}
                designer={designer}
                contextData={contextData}
                mapConfig={mapConfig}
                mapData={mapData}
                onSelectMarkerPosition={onMarkPosition}
            />

            <div className="map-header">
                <SelectableItem
                    designer={designer}
                    props={selectUIProps(uiProps, XPRProps, 'headerContainer', 'title')}
                    contextData={contextData}
                >
                    <div className="uxp-widget-title-bar uxp-drag-handler" style={convertColorStyles(headerStyles, contextData)}>
                        <div className="title-section" style={titleStyles}>
                            <span className="title-icon"></span>
                            <span className="txt">{title}</span>
                        </div>
                        <div className="action-section"></div>
                    </div>
                </SelectableItem>
            </div>

        </SelectableItem>
    </WidgetWrapper>
};


registerCardTemplate({
    id: 'map-widget',
    name: 'Map Widget',
    description: 'A map with markers on it. Either use a global satellite map or specify your own tile server and data source to pull and show coordinates on the map. Configure cusotm content to show when clicking on a marker.',
    template: MapWidget,
    icon: generateIconProp('mapMarked'),
    canDragDropFields: true,
    complexity: 'hard',

});